/**
 * Zentrale Query-Key-Factory für TanStack Query.
 *
 * Warum: Mutationen müssen alle betroffenen Caches invalidieren. Mit
 * hierarchischen Keys reicht `invalidateQueries({ queryKey: orderKeys.all })`,
 * um Liste, Detail und History gleichzeitig zu treffen (Prefix-Matching).
 * Vorher wurden Keys ad hoc als String-Arrays verstreut — die Orders-Liste
 * (`['orders', filters]`) wurde nach Detail-Aktionen nie invalidiert, und
 * `tenantSettings` vs. `tenant-settings` waren zwei getrennte Caches.
 */
export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  /** `filters` enthält u. a. Seite, Sortierung, Status und (N08) `tenant_id` für GLOBAL_ADMIN. */
  list: (filters: unknown) => [...orderKeys.lists(), filters] as const,
  detail: (id: string | undefined) => [...orderKeys.all, 'detail', id] as const,
  history: (id: string | undefined) => [...orderKeys.detail(id), 'history'] as const,
  pdf: (pdfId: string | undefined) => [...orderKeys.all, 'pdf', pdfId] as const,
};

export const userKeys = {
  all: ['users'] as const,
  list: (filters: unknown) => [...userKeys.all, 'list', filters] as const,
};

export const tenantKeys = {
  all: ['tenant'] as const,
  /** Mandantenliste (GLOBAL_ADMIN) – für Mandantenspalte/-filter und Detail-Chip (N08) */
  list: () => [...tenantKeys.all, 'list'] as const,
  settings: (tenantId: string | undefined) => [...tenantKeys.all, 'settings', tenantId] as const,
};

export const availabilityKeys = {
  all: ['availability'] as const,
  rules: () => [...availabilityKeys.all, 'rules'] as const,
  blackouts: () => [...availabilityKeys.all, 'blackouts'] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  overview: () => [...dashboardKeys.all, 'overview'] as const,
};

export const calendarKeys = {
  all: ['calendar'] as const,
  appointments: (range?: { start_date: string; end_date: string }) => [...calendarKeys.all, 'appointments', ...(range ? [range] : [])] as const,
};

export const subcontractorKeys = {
  all: ['subcontractors'] as const,
  lists: () => [...subcontractorKeys.all, 'list'] as const,
  list: (filters: unknown) => [...subcontractorKeys.lists(), filters] as const,
  detail: (id: string | undefined) => [...subcontractorKeys.all, 'detail', id] as const,
  suggestion: (orderId: string | undefined) => [...subcontractorKeys.all, 'suggestion', orderId] as const,
  route: (date: string, subcontractorId?: string) =>
    [...subcontractorKeys.all, 'route', date, subcontractorId] as const,
  notifyStatus: (orderId: string) => [...subcontractorKeys.all, 'notify-status', orderId] as const,
};

/**
 * Alle Caches, die von einer Auftrags-Mutation abhängen:
 * Liste + Detail + History, Dashboard-Kennzahlen, Kalender sowie Subunternehmer
 * mit Vorschlägen, Routen und Benachrichtigungsstatus.
 */
export const orderDependentKeys = [orderKeys.all, dashboardKeys.all, calendarKeys.all, subcontractorKeys.all] as const;
